import { TextInput } from '@mantine/core';
import { Modal } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useState } from 'react';

import styles from './EmailChangeModal.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import {
    useConfirmEmailChangeMutation,
    useRefreshTokenMutation,
    useRequestEmailChangeMutation,
} from 'api/accountEndpoints';
import useGetUser from 'hooks/useGetUser';
import { EMAIL_PATTERN } from 'utils/constants';
import { setToken } from 'store/reducers/authSlice';
import { AppDispatch } from 'store/store';

type Props = {
    setModalOpen: (value: boolean) => void;
    modalOpen: boolean;
}

type Step = 'email' | 'code' | 'success';

const EmailChangeModal = ({
    setModalOpen,
    modalOpen,
}: Props) => {
    const intl = useIntl();
    const dispatch = useDispatch<AppDispatch>();
    const user = useGetUser();
    const [newEmail, setNewEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [step, setStep] = useState<Step>('email');
    const [codeError, setCodeError] = useState('');
    const [requestEmailChange, { isLoading: isRequesting }] = useRequestEmailChangeMutation();
    const [confirmEmailChange, { isLoading: isConfirming }] = useConfirmEmailChangeMutation();
    const [refreshToken, { isLoading: isRefreshingToken }] = useRefreshTokenMutation();

    const trimmedEmail = newEmail.trim();
    const isEmailInvalid = trimmedEmail.length > 0 && !EMAIL_PATTERN.test(trimmedEmail);
    const isSameEmail = user?.email.toLowerCase() === trimmedEmail.toLowerCase();
    const isLoading = isRequesting || isConfirming || isRefreshingToken;
    const isEmailSubmitDisabled = !trimmedEmail || isEmailInvalid || isSameEmail || isLoading;
    const isCodeSubmitDisabled = !verificationCode.trim() || isLoading;

    const reset = () => {
        setNewEmail('');
        setVerificationCode('');
        setStep('email');
        setCodeError('');
    };

    const onModalClose = () => {
        setModalOpen(false);
        reset();
    };

    const onSubmit = async () => {
        if (step === 'success') {
            onModalClose();
            return;
        }

        if (step === 'email') {
            await requestEmailChange({ newEmail: trimmedEmail })
                .unwrap()
                .then(() => {
                    setStep('code');
                    setCodeError('');
                })
                .catch(() => { /* handled in rtk query extraOptions */ });
            return;
        }

        await confirmEmailChange({ code: verificationCode.trim() })
            .unwrap()
            .then(async () => {
                const refreshResult = await refreshToken().unwrap();
                if (refreshResult.token) {
                    dispatch(setToken(refreshResult.token));
                }
                setStep('success');
            })
            .catch(() => {
                setCodeError(intl.formatMessage(messages.invalidCode));
            });
    };

    const primaryButtonText = (() => {
        if (step === 'code') {
            return intl.formatMessage(messages.confirm);
        }

        if (step === 'success') {
            return intl.formatMessage(sharedMessages.close);
        }

        return intl.formatMessage(sharedMessages.save);
    })();

    const primaryButtonDisabled = (() => {
        if (step === 'email') {
            return isEmailSubmitDisabled;
        }

        if (step === 'code') {
            return isCodeSubmitDisabled;
        }

        return false;
    })();
    const emailError = (() => {
        if (isEmailInvalid) {
            return intl.formatMessage(messages.newEmailWarning);
        }

        if (isSameEmail) {
            return intl.formatMessage(messages.sameEmailWarning);
        }

        return undefined;
    })();

    return (
        <Modal
            open={modalOpen}
            onRequestClose={onModalClose}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onRequestSubmit={onSubmit}
            modalHeading={intl.formatMessage(messages.changeEmail)}
            modalLabel={intl.formatMessage(messages.changeEmailHeading)}
            primaryButtonText={primaryButtonText}
            primaryButtonDisabled={primaryButtonDisabled}
            loadingStatus={isLoading ? 'active' : undefined}
            closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            secondaryButtonText={intl.formatMessage(step === 'success' ? sharedMessages.close : sharedMessages.back)}
        >
            {step === 'email' && (
                <>
                    <p className={styles.info}>
                        {intl.formatMessage(messages.emailStepInfo)}
                    </p>
                    <TextInput
                        id="email-input-in-settings-modal"
                        label={intl.formatMessage(messages.newEmail)}
                        error={emailError}
                        onChange={(e) => { setNewEmail(e.target.value); }}
                        value={newEmail}
                        size="md"
                    />
                </>
            )}
            {step === 'code' && (
                <>
                    <p className={styles.info}>
                        {intl.formatMessage(messages.codeSentInfo, { email: user?.email ?? '' })}
                    </p>
                    <TextInput
                        id="email-verification-code-input-in-settings"
                        label={intl.formatMessage(messages.verificationCode)}
                        error={codeError || undefined}
                        onChange={(e) => {
                            setCodeError('');
                            setVerificationCode(e.target.value);
                        }}
                        value={verificationCode}
                        size="md"
                    />
                </>
            )}
            {step === 'success' && (
                <p className={styles.info}>
                    {intl.formatMessage(messages.successInfo)}
                </p>
            )}
        </Modal>
    );
};

const messages = defineMessages({
    changeEmail: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.changeEmail',
        defaultMessage: 'Zmiana adresu e-mail',
    },
    changeEmailHeading: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.changeEmailHeading',
        defaultMessage: 'Zmień e-mail konta.',
    },
    emailStepInfo: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.emailStepInfo',
        defaultMessage: 'Wpisz nowy adres e-mail. Kod potwierdzający wyślemy na obecny adres e-mail konta.',
    },
    newEmail: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.newEmail',
        defaultMessage: 'Nowy e-mail',
    },
    newEmailWarning: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.newEmailWarning',
        defaultMessage: 'Niepoprawny e-mail.',
    },
    sameEmailWarning: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.sameEmailWarning',
        defaultMessage: 'Podaj adres inny niż aktualny.',
    },
    codeSentInfo: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.codeSentInfo',
        defaultMessage: 'Kod został wysłany na obecny adres e-mail: {email}. Jest ważny 10 minut.',
    },
    verificationCode: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.verificationCode',
        defaultMessage: 'Kod potwierdzający',
    },
    invalidCode: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.invalidCode',
        defaultMessage: 'Nieprawidłowy kod. Sprawdź i spróbuj ponownie.',
    },
    successInfo: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.successInfo',
        defaultMessage: 'Adres e-mail został zmieniony. Dane użytkownika zostały odświeżone.',
    },
    confirm: {
        id: 'src.Screens.Dashboard.Settings.Options.EmailChangeButton.EmailChangeModal.confirm',
        defaultMessage: 'Zatwierdź',
    },
});

export default EmailChangeModal;
